import type { LocationPickerData, LocationRef } from './locationPickerModel';

/**
 * Where a picked location sits (#472 S8): its address and floor, resolved
 * from the lists the picker already holds. Door, outlet and filter callers
 * store `address_id` + `floor_id` + `room_id`, so the picker's `onChange`
 * passes this next to the plain `{ kind, id }` ref.
 */

export interface LocationPathNode {
  id: number;
  name: string;
}

export interface LocationPathRoom extends LocationPathNode {
  /** What people call it ("Townhouse"); `name` is the number. */
  description?: string;
}

export interface LocationPath {
  address?: LocationPathNode;
  floor?: LocationPathNode;
  room?: LocationPathRoom;
  /** Set for a picked door (with the place it sits at above). */
  door?: LocationPathNode;
}

const same = (a: number | string | null | undefined, b: number | string | null | undefined) =>
  a != null && b != null && String(a) === String(b);

/** The path of one ref; levels that aren't known are left out. */
export function locationPath(ref: LocationRef, { addresses = [], floors = [], rooms = [], doors = [] }: LocationPickerData): LocationPath {
  const addressNode = (id: number | string | null | undefined, nestedName?: string): LocationPathNode | undefined => {
    if (id == null || id === '') return undefined;
    const listed = addresses.find((a) => same(a.id, id));
    const name = listed?.name ?? nestedName;
    return { id: Number(id), name: (name ?? '').trim() };
  };
  const floorPath = (id: number | string): Pick<LocationPath, 'address' | 'floor'> => {
    const listed = floors.find((f) => same(f.id, id));
    if (listed) {
      return {
        floor: { id: Number(listed.id), name: listed.name },
        address: addressNode(listed.address?.id ?? listed.address_id, listed.address?.name),
      };
    }
    const viaRoom = rooms.find((r) => same(r.floor?.id, id))?.floor;
    return {
      floor: { id: Number(id), name: viaRoom?.name ?? '' },
      address: viaRoom?.address ? addressNode(viaRoom.address.id, viaRoom.address.name) : undefined,
    };
  };

  const prune = (path: LocationPath): LocationPath =>
    Object.fromEntries(Object.entries(path).filter(([, node]) => node != null)) as LocationPath;

  switch (ref.kind) {
    case 'address':
      return prune({ address: addressNode(ref.id) });
    case 'floor':
      return prune(floorPath(ref.id));
    case 'door': {
      const door = doors.find((d) => same(d.id, ref.id));
      const doorNode: LocationPathNode = { id: ref.id, name: (door?.name ?? '').trim() };
      if (!door) return { door: doorNode };
      const floorRef = door.floor ?? door.room?.floor;
      const viaFloor = floorRef ? floorPath(floorRef.id) : undefined;
      const description = door.room?.description?.trim();
      return prune({
        address:
          addressNode(door.address?.id, door.address?.name) ??
          viaFloor?.address ??
          (floorRef?.address ? addressNode(floorRef.address.id, floorRef.address.name) : undefined),
        floor: floorRef ? (viaFloor?.floor?.name ? viaFloor.floor : { id: Number(floorRef.id), name: floorRef.name.trim() }) : undefined,
        room: door.room
          ? { id: Number(door.room.id), name: door.room.name.trim(), ...(description ? { description } : {}) }
          : undefined,
        door: doorNode,
      });
    }
    case 'room': {
      const room = rooms.find((r) => same(r.id, ref.id));
      const description = room?.description?.trim();
      const roomNode: LocationPathRoom = { id: ref.id, name: (room?.name ?? '').trim(), ...(description ? { description } : {}) };
      if (!room?.floor) return { room: roomNode };
      const { address, floor } = floorPath(room.floor.id);
      return prune({
        address: address ?? (room.floor.address ? addressNode(room.floor.address.id, room.floor.address.name) : undefined),
        floor,
        room: roomNode,
      });
    }
  }
}

/** The paths of several refs, in the same order. */
export const locationPaths = (refs: readonly LocationRef[], data: LocationPickerData): LocationPath[] =>
  refs.map((ref) => locationPath(ref, data));
